import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Accordion, AccordionSummary, AccordionDetails, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockResetIcon from "@mui/icons-material/LockReset";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { SETTING_TABLE_NAME } from "../../../constants/settingsTableName";
import { useSystemConfig, useSystemId } from "../../../hooks/useReduxSelectors";
import { useMsal } from "Auth/msal-context";
import useSettings from "../../../hooks/useSettings";
import { clearEdgePin } from "../../../ducks/authenticationDuck";
import { pincodeApi } from "../../../actions/apiActions";
import ChangePinDialog from "./ChangePinDialog";
import LocalPasswordDialog from "../../dashboardTopBar/LocalPasswordDialog";

const GeneralSettingView = () => {
  const systemId = useSystemId();
  const { i18n, t } = useTranslation("common");
  const { isAdmin, isAuthorized, isEdge } = useMsal();
  const config = useSystemConfig();
  const [changePinOpen, setChangePinOpen] = useState(false);
  const [pinInfoOpen, setPinInfoOpen] = useState(false);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [removePinInputOpen, setRemovePinInputOpen] = useState(false);
  const [removingPin, setRemovingPin] = useState(false);
  const [pinStatusChecking, setPinStatusChecking] = useState(false);
  const [pinEnabled, setPinEnabled] = useState(null); // null = unknown

  const openRemoveConfirm = async () => {
    if (!config) return;
    setPinEnabled(null);
    setRemoveConfirmOpen(true);
    setPinStatusChecking(true);
    try {
      const status = await pincodeApi.getStatus(config.SETTING_URL);
      setPinEnabled(status?.pincodeEnabled ?? false);
    } catch {
      setPinEnabled(false);
    } finally {
      setPinStatusChecking(false);
    }
  };

  const dispatch = useDispatch();
  const { formState, updateField, loading, error, fetchSettings, saveSettings } =
    useSettings(SETTING_TABLE_NAME.GENERAL_SETTING);

  const doRemovePin = async () => {
    if (!config) return;
    setRemovingPin(true);
    try {
      await pincodeApi.remove(config.SETTING_URL);
      dispatch(clearEdgePin());
    } finally {
      setRemovingPin(false);
    }
  };

  const handleSave = () => {
    if (!formState) return;
    saveSettings(formState).then(() => {
      if (formState.language !== i18n.language) {
        i18n.changeLanguage(formState.language);
      }
    });
  };

  return (
    <Accordion sx={{ mb: 2 }} disableGutters onChange={(e, expanded) => { if (expanded) fetchSettings(); }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "primary.main", color: "common.white" }}>
        <Typography sx={{ fontWeight: "bold", fontSize: "1.5rem" }}>{t("General Settings")}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ bgcolor: "background.light", p: 2 }}>
        {loading && <CircularProgress size={24} />}
        {error && (
          <Typography sx={{ textAlign: "center", color: "secondary.main" }}>{t("No Settings Found")}</Typography>
        )}
        {!loading && formState && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                variant="standard"
                label={t("System Name")}
                value={formState.systemName ?? ""}
                slotProps={{ input: { readOnly: true } }}
                fullWidth sx={{ mb: 1 }}
              />
              {(isAdmin || config?.APP_ENV === "Edge") && (
                <TextField variant="standard" label={t("System ID")} value={systemId} slotProps={{ input: { readOnly: true } }} fullWidth />
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField variant="standard" label={t("Project Number")} value={formState.projectNumber ?? ""} slotProps={{ input: { readOnly: true } }} fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography variant="body2">{t("Language")}</Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    variant={formState.language === "fi" ? "contained" : "outlined"}
                    color={formState.language === "fi" ? "secondary" : "primary"}
                    onClick={() => updateField("language", "fi")}
                  >
                    {t("Finnish")}
                  </Button>
                  <Button
                    size="small"
                    variant={formState.language === "en" ? "contained" : "outlined"}
                    color={formState.language === "en" ? "secondary" : "primary"}
                    onClick={() => updateField("language", "en")}
                  >
                    {t("English")}
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField variant="standard" label={t("Solution")} value={formState.solution ?? ""} slotProps={{ input: { readOnly: true } }} fullWidth />
            </Grid>
          </Grid>
        )}
        {(isAuthorized || isEdge) && formState && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 1 }}>
            {isEdge && (
              <Tooltip title={t("PIN Info")}>
                <IconButton size="small" onClick={() => setPinInfoOpen(true)}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isEdge && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<LockResetIcon />}
                onClick={() => setChangePinOpen(true)}
              >
                {t("Create or Change Pincode")}
              </Button>
            )}
            {isEdge && (
              <Button
                variant="outlined"
                size="small"
                color="warning"
                startIcon={<LockOpenIcon />}
                onClick={() => dispatch(clearEdgePin())}
              >
                {t("Lock Locally")}
              </Button>
            )}
            {isEdge && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={removingPin ? <CircularProgress size={14} /> : <DeleteForeverIcon />}
                onClick={() => openRemoveConfirm()}
                disabled={removingPin}
              >
                {t("Remove Pincode")}
              </Button>
            )}
            <Button variant="contained" size="small" onClick={handleSave}>{t("Save")}</Button>
          </Box>
        )}
        {isEdge && <ChangePinDialog open={changePinOpen} onClose={() => setChangePinOpen(false)} baseUrl={config?.SETTING_URL ?? ""} />}

        {/* PIN info dialog */}
        <Dialog open={pinInfoOpen} onClose={() => setPinInfoOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{t("PIN Code Info")}</DialogTitle>
          <DialogContent>
            <Typography sx={{ whiteSpace: "pre-line" }}>{t("PIN Code Description")}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPinInfoOpen(false)} variant="contained">{t("Close")}</Button>
          </DialogActions>
        </Dialog>

        {/* Remove PIN — confirmation step */}
        <Dialog open={removeConfirmOpen} onClose={() => setRemoveConfirmOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{t("Remove Pincode")}</DialogTitle>
          <DialogContent>
            {pinStatusChecking && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            {!pinStatusChecking && !pinEnabled && (
              <Typography>{t("No PIN In Use")}</Typography>
            )}
            {!pinStatusChecking && pinEnabled && (
              <Typography>{t("Remove Pincode Confirm")}</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRemoveConfirmOpen(false)}>{t("Cancel")}</Button>
            {!pinStatusChecking && pinEnabled && (
              <Button
                color="error"
                variant="contained"
                onClick={() => { setRemoveConfirmOpen(false); setRemovePinInputOpen(true); }}
              >
                {t("Continue")}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Remove PIN — verify current PIN */}
        {isEdge && (
          <LocalPasswordDialog
            open={removePinInputOpen}
            onClose={() => setRemovePinInputOpen(false)}
            onSuccess={doRemovePin}
            baseUrl={config?.SETTING_URL ?? ""}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default GeneralSettingView;
